import { useMemo, useState } from 'react';
import SegmentedTabs from '../../reusable/SegmentedTabs/SegmentedTabs';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import NotificationGlyph from '../../reusable/NotificationGlyph/NotificationGlyph';
import {
  toNotification,
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from '../../store/api/notificationsApi';
import { debounce } from '../../utilities/debounce';
import { t } from '../../i18n/i18n';
import './Notifications.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** The search box settles before it asks the API — same as the top bar's. */
const SEARCH_DELAY = 300;

/**
 * How many rows a request brings back, and how many more "Load older" asks for.
 *
 * ponytail: the tiles count this window rather than the whole feed — the
 * endpoint pages, and the only total it hands back is `count`. That is exact
 * whenever the window already holds everything, which is the case for a normal
 * inbox, and the "Load older" line says when it doesn't. Swap to real totals if
 * the API ever grows an unread filter.
 */
const PAGE_STEP = 25;

const DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const fullDate = (value) => {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? '' : DATE_FORMATTER.format(ms);
};

const VIEWS = [
  { label: 'All', value: 'all' },
  { label: 'Unread', value: 'unread' },
];

/**
 * One counter above the inbox. `tone` is the same vocabulary the rows use, so a
 * tile and the rows it counts are drawn in one colour rather than two.
 */
function Tile({ tone, label, value, loading }) {
  return (
    <div className={`notif-tile notif-tile--${tone}`}>
      <span className="notif-tile__glyph">
        {/* "unread" is not one of the row tones, so the glyph falls through to
            the dot — which is the mark the rows and the bell already use for
            it. */}
        {tone === 'total' ? (
          <span className="icon" style={icon('topbar-bell')} />
        ) : (
          <NotificationGlyph tone={tone} />
        )}
      </span>
      <span className="notif-tile__copy">
        <span className="notif-tile__label">{t(label)}</span>
        {loading ? <Skeleton w="34px" h={18} /> : <span className="notif-tile__value">{value}</span>}
      </span>
    </div>
  );
}

/**
 * Notifications — the whole feed, where the bell only carries the last five.
 *
 * Two panes: the list picks, the reader shows. Opening a message is what marks
 * it read, so the unread count answers to what has actually been looked at
 * rather than to whether a panel was ever opened — the same rule the bell's dot
 * follows.
 *
 * No Figma node: the Style Guide has no inbox screen yet. The furniture is the
 * app's own — `page-head`, the surface/raised token steps, the tone colours the
 * bell's dropdown already uses — so it reads as this app rather than as a new
 * one bolted on.
 */
export default function Notifications() {
  const [view, setView] = useState('all');
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_STEP);
  const [selectedId, setSelectedId] = useState(null);

  const applySearch = useMemo(
    () => debounce((value) => setSearch(value.trim()), SEARCH_DELAY),
    [],
  );

  const { data, isLoading, isFetching } = useGetNotificationsQuery({
    page: 1,
    page_size: pageSize,
    search,
  });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();

  const rows = useMemo(() => (data?.results ?? []).map(toNotification), [data]);

  const total = data?.count ?? rows.length;
  const unread = rows.filter((row) => !row.read).length;

  /* The unread view keeps whatever is open on screen. Without that exception
     the row just clicked would vanish under the pointer — opening it is what
     marks it read — and the reader would jump to someone else's message. */
  const visible = rows.filter((row) => view === 'all' || !row.read || row.id === selectedId);
  const selected = visible.find((row) => row.id === selectedId) ?? visible[0] ?? null;

  const open = (row) => {
    setSelectedId(row.id);
    if (!row.read) markRead(row.id);
  };

  return (
    <div className="notif">
      <header className="page-head">
        <h1 className="page-head__title">{t('Notifications')}</h1>
        <p className="page-head__meta">
          {t(
            'Everything the platform has sent you — payouts, account milestones and anything that needs a look.',
          )}
        </p>
      </header>

      <div className="notif-tiles">
        <Tile tone="total" label="All notifications" value={total} loading={isLoading} />
        <Tile tone="unread" label="Unread" value={unread} loading={isLoading} />
        {/* No "Approved" / "Needs attention" counters (TI review E5): the feed is
            mostly announcements and offers, so a tone guessed from each
            message's wording made a count that didn't fit — and "Approved" read
            like an internal payout status. */}
      </div>

      <div className="notif-split">
        <section className="notif-list" aria-label={t('Notification list')}>
          <header className="notif-list__head">
            <SegmentedTabs options={VIEWS} value={view} onChange={setView} />
            <button
              type="button"
              className="notif-list__mark"
              disabled={!unread}
              onClick={() => markAllRead()}
            >
              {t('Mark all read')}
            </button>
          </header>

          <label className="notif-search">
            <span className="icon" style={icon('table-search')} />
            <input
              type="search"
              placeholder={t('Search notifications')}
              aria-label={t('Search notifications')}
              value={term}
              onChange={(e) => {
                setTerm(e.target.value);
                applySearch(e.target.value);
              }}
            />
          </label>

          <div className="notif-list__rows">
            {isLoading &&
              Array.from({ length: 5 }, (_, i) => (
                <div className="notif-row is-skeleton" key={i}>
                  <Skeleton w="32px" h={32} radius={8} />
                  <span className="notif-row__copy">
                    <Skeleton w="60%" h={11} />
                    <Skeleton w="85%" h={10} />
                  </span>
                </div>
              ))}

            {!isLoading &&
              visible.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  className={[
                    'notif-row',
                    row.read ? '' : 'is-unread',
                    selected?.id === row.id ? 'is-selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => open(row)}
                >
                  <span className={`notif-row__glyph notif-row__glyph--${row.type}`}>
                    <NotificationGlyph tone={row.type} />
                  </span>
                  <span className="notif-row__copy">
                    <span className="notif-row__title">{row.title}</span>
                    <span className="notif-row__excerpt">{row.message}</span>
                    <span className="notif-row__time">{row.time}</span>
                  </span>
                  {!row.read && <span className="notif-row__dot" aria-label={t('Unread')} />}
                </button>
              ))}

            {!isLoading && visible.length === 0 && (
              <p className="notif-empty">
                {search && t('Nothing matches “{{search}}”.', { search })}
                {!search && view === 'unread' && t('You are all caught up.')}
                {!search && view === 'all' && t('Nothing has come through yet.')}
              </p>
            )}
          </div>

          {/* Only when the server is holding more than we are — a button that
              re-fetches the same rows is a button that does nothing. */}
          {rows.length < total && !search && (
            <button
              type="button"
              className="notif-list__more"
              disabled={isFetching}
              onClick={() => setPageSize((size) => size + PAGE_STEP)}
            >
              {isFetching ? t('Loading…') : t('Load older ({{n}} left)', { n: total - rows.length })}
            </button>
          )}
        </section>

        <section className="notif-reader" aria-label={t('Notification')}>
          {selected && (
            <>
              <header className="notif-reader__head">
                <span className={`notif-reader__glyph notif-reader__glyph--${selected.type}`}>
                  <NotificationGlyph tone={selected.type} />
                </span>
                <span className="notif-reader__heading">
                  <h2 className="notif-reader__title">{selected.title}</h2>
                  <span className="notif-reader__date">{fullDate(selected.sentAt)}</span>
                </span>
              </header>

              <p className="notif-reader__body">{selected.message}</p>
            </>
          )}

          {!selected && isLoading && (
            <div className="notif-reader__skeleton">
              <Skeleton w="40%" h={16} />
              <Skeleton w="22%" h={10} />
              <Skeleton w="100%" h={11} />
              <Skeleton w="90%" h={11} />
            </div>
          )}

          {!selected && !isLoading && (
            <p className="notif-empty notif-empty--reader">{t('Pick a notification to read it here.')}</p>
          )}
        </section>
      </div>
    </div>
  );
}
